<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link :to="backUrl" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <div class="card">
      <div class="card-header">
        <h6 class="card-title mb-0">Add Test</h6>
        <small v-if="parentData" class="text-muted">
          Attached to: {{ parentTypeLabel }} - {{ parentData.name || parentData.title }}
        </small>
      </div>
      <div class="card-body">
        <form @submit.prevent="handleSubmit">
          <div class="row">
            <div class="col-lg-8">
              <!-- Parent Selection (if not coming from a parent route) -->
              <div v-if="!hasParentFromRoute" class="parent-selection-section mb-4">
                <h6 class="section-title">
                  <vue-feather type="link" size="16" class="me-2" />
                  Attach Test To <span class="text-danger">*</span>
                </h6>
                <p class="text-muted small mb-3">Tests must be attached to a Test Series, Topic, or Lesson</p>
                
                <div class="parent-type-selector mb-3">
                  <div 
                    v-for="type in parentTypes" 
                    :key="type.value"
                    :class="['parent-type-option', { active: selectedParentType === type.value }]"
                    @click="selectParentType(type.value)"
                  >
                    <vue-feather :type="type.icon" size="20" />
                    <span>{{ type.label }}</span>
                  </div>
                </div>

                <!-- Cascading Selectors -->
                <div v-if="selectedParentType" class="cascading-selectors">
                  <!-- Exam Selection (always first) -->
                  <div class="mb-3">
                    <label class="form-label">Select Exam <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="selectedExamId" @change="onExamChange" required>
                      <option value="">Choose an exam...</option>
                      <option v-for="exam in exams" :key="exam.id" :value="exam.id">{{ exam.name }}</option>
                    </select>
                  </div>

                  <!-- Test Series Selection -->
                  <div v-if="selectedParentType === 'series' && selectedExamId" class="mb-3">
                    <label class="form-label">Select Test Series <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="selectedSeriesId" :disabled="loadingOptions" required>
                      <option value="">Choose a test series...</option>
                      <option v-for="series in testSeriesList" :key="series.id" :value="series.id">{{ series.name }}</option>
                    </select>
                  </div>

                  <!-- Subject Selection (for topic/lesson) -->
                  <div v-if="(selectedParentType === 'topic' || selectedParentType === 'lesson') && selectedExamId" class="mb-3">
                    <label class="form-label">Select Subject <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="selectedSubjectId" @change="onSubjectChange" :disabled="loadingOptions" required>
                      <option value="">Choose a subject...</option>
                      <option v-for="subject in subjects" :key="subject.id" :value="subject.id">{{ subject.name }}</option>
                    </select>
                  </div>

                  <!-- Topic Selection -->
                  <div v-if="(selectedParentType === 'topic' || selectedParentType === 'lesson') && selectedSubjectId" class="mb-3">
                    <label class="form-label">Select Topic <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="selectedTopicId" @change="onTopicChange" :disabled="loadingOptions" required>
                      <option value="">Choose a topic...</option>
                      <option v-for="topic in topics" :key="topic.id" :value="topic.id">{{ topic.name }}</option>
                    </select>
                  </div>

                  <!-- Lesson Selection -->
                  <div v-if="selectedParentType === 'lesson' && selectedTopicId" class="mb-3">
                    <label class="form-label">Select Lesson <span class="text-danger">*</span></label>
                    <select class="form-select" v-model="selectedLessonId" :disabled="loadingOptions" required>
                      <option value="">Choose a lesson...</option>
                      <option v-for="lesson in lessons" :key="lesson.id" :value="lesson.id">{{ lesson.title }}</option>
                    </select>
                  </div>
                </div>

                <div v-if="!isParentSelected" class="alert alert-warning">
                  <vue-feather type="alert-triangle" size="16" class="me-2" />
                  Please select where to attach this test
                </div>
              </div>

              <hr v-if="!hasParentFromRoute" class="my-4" />

              <!-- Test Details -->
              <div class="mb-3">
                <label class="form-label">Test Title <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.title" required placeholder="e.g., Chapter 1 Quiz" />
              </div>

              <div class="mb-3">
                <label class="form-label">Description</label>
                <textarea class="form-control" v-model="form.description" rows="3" placeholder="Describe this test..."></textarea>
              </div>

              <div class="row">
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Duration (minutes) <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" v-model="form.duration" min="1" required />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Total Marks <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" v-model="form.totalMarks" min="1" required />
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="mb-3">
                    <label class="form-label">Passing Marks <span class="text-danger">*</span></label>
                    <input type="number" class="form-control" v-model="form.passingMarks" min="0" required />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Negative Marking</label>
                    <input type="number" class="form-control" v-model="form.negativeMarking" min="0" step="0.25" placeholder="0" />
                    <small class="text-muted">Marks deducted per wrong answer</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Status</label>
                    <select class="form-select" v-model="form.status">
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <div class="form-check">
                      <input type="checkbox" class="form-check-input" id="shuffleQuestions" v-model="form.shuffleQuestions" />
                      <label class="form-check-label" for="shuffleQuestions">Shuffle Questions</label>
                    </div>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Show Answers</label>
                    <select class="form-select" v-model="form.showAnswers">
                      <option value="immediately">Immediately after submission</option>
                      <option value="after_submit">After test ends</option>
                      <option value="never">Never</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="d-flex gap-2 pt-2">
                <button type="submit" class="btn btn-primary" :disabled="loading || !canSubmit">
                  <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                  Create Test
                </button>
                <router-link :to="backUrl" class="btn btn-outline-secondary">Cancel</router-link>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'AddTest',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    
    // Route params (if coming from a parent page)
    const lessonIdFromRoute = route.params.lessonId
    const seriesIdFromRoute = route.params.seriesId
    const topicIdFromRoute = route.params.topicId

    const loading = ref(false)
    const loadingOptions = ref(false)
    const parentData = ref(null)
    
    // Parent selection state
    const selectedParentType = ref(null)
    const selectedExamId = ref('')
    const selectedSeriesId = ref('')
    const selectedSubjectId = ref('')
    const selectedTopicId = ref('')
    const selectedLessonId = ref('')
    
    // Options lists
    const exams = ref([])
    const testSeriesList = ref([])
    const subjects = ref([])
    const topics = ref([])
    const lessons = ref([])

    const parentTypes = [
      { value: 'series', label: 'Test Series', icon: 'layers' },
      { value: 'topic', label: 'Topic', icon: 'book' },
      { value: 'lesson', label: 'Lesson', icon: 'file-text' }
    ]

    const form = ref({
      title: '',
      description: '',
      duration: 30,
      totalMarks: 100,
      passingMarks: 40,
      negativeMarking: 0,
      shuffleQuestions: false,
      showAnswers: 'after_submit',
      status: 'draft',
      scheduledAt: ''
    })

    // Computed properties
    const hasParentFromRoute = computed(() => {
      return !!(lessonIdFromRoute || seriesIdFromRoute || topicIdFromRoute)
    })

    const parentType = computed(() => {
      if (lessonIdFromRoute) return 'lesson'
      if (seriesIdFromRoute) return 'series'
      if (topicIdFromRoute) return 'topic'
      return selectedParentType.value
    })

    const parentTypeLabel = computed(() => {
      const labels = { series: 'Test Series', topic: 'Topic', lesson: 'Lesson' }
      return labels[parentType.value] || ''
    })

    const isParentSelected = computed(() => {
      if (hasParentFromRoute.value) return true
      if (!selectedParentType.value) return false
      
      switch (selectedParentType.value) {
        case 'series': return !!selectedSeriesId.value
        case 'topic': return !!selectedTopicId.value
        case 'lesson': return !!selectedLessonId.value
        default: return false
      }
    })

    const canSubmit = computed(() => {
      return isParentSelected.value && form.value.title && form.value.duration
    })

    const backUrl = computed(() => {
      if (lessonIdFromRoute) return `/app/lessons/${lessonIdFromRoute}/tests`
      if (seriesIdFromRoute) return `/app/test-series/${seriesIdFromRoute}/tests`
      if (topicIdFromRoute) return `/app/topics/${topicIdFromRoute}/tests`
      
      if (selectedLessonId.value) return `/app/lessons/${selectedLessonId.value}/tests`
      if (selectedSeriesId.value) return `/app/test-series/${selectedSeriesId.value}/tests`
      if (selectedTopicId.value) return `/app/topics/${selectedTopicId.value}/tests`
      
      return '/app/dashboard'
    })

    const breadcrumbs = computed(() => {
      const items = [
        { title: 'Dashboard', url: '/app/dashboard' },
        { title: 'Exams', url: '/app/exams/list' }
      ]
      
      if (parentType.value === 'lesson') {
        if (parentData.value?.examId) {
          items.push({ title: parentData.value?.examName || 'Exam', url: `/app/exams/${parentData.value.examId}/subjects` })
        }
        if (parentData.value?.subjectId) {
          items.push({ title: parentData.value?.subjectName || 'Subject', url: `/app/subjects/${parentData.value.subjectId}/topics` })
        }
        if (parentData.value?.topicId) {
          items.push({ title: parentData.value?.topicName || 'Topic', url: `/app/topics/${parentData.value.topicId}/lessons` })
        }
        const lessonId = lessonIdFromRoute || selectedLessonId.value
        items.push({ title: parentData.value?.title || 'Lesson', url: `/app/lessons/${lessonId}/tests` })
      } else if (parentType.value === 'series') {
        if (parentData.value?.examId) {
          items.push({ title: parentData.value?.examName || 'Exam', url: `/app/exams/${parentData.value.examId}/test-series` })
        }
        const seriesId = seriesIdFromRoute || selectedSeriesId.value
        items.push({ title: parentData.value?.name || 'Test Series', url: `/app/test-series/${seriesId}/tests` })
      } else if (parentType.value === 'topic') {
        if (parentData.value?.examId) {
          items.push({ title: parentData.value?.examName || 'Exam', url: `/app/exams/${parentData.value.examId}/subjects` })
        }
        if (parentData.value?.subjectId) {
          items.push({ title: parentData.value?.subjectName || 'Subject', url: `/app/subjects/${parentData.value.subjectId}/topics` })
        }
        const topicId = topicIdFromRoute || selectedTopicId.value
        items.push({ title: parentData.value?.name || 'Topic', url: `/app/topics/${topicId}/tests` })
      }
      
      items.push({ title: 'Add Test', active: true })
      return items
    })

    // Methods
    const fetchExams = async () => {
      try {
        const res = await api.get('/v1/admin/exams')
        exams.value = res.data.exams || []
      } catch (err) {
        console.error('Failed to fetch exams:', err)
      }
    }

    const selectParentType = (type) => {
      selectedParentType.value = type
      // Reset selections
      selectedSeriesId.value = ''
      selectedSubjectId.value = ''
      selectedTopicId.value = ''
      selectedLessonId.value = ''
      testSeriesList.value = []
      subjects.value = []
      topics.value = []
      lessons.value = []
    }

    const onExamChange = async () => {
      selectedSeriesId.value = ''
      selectedSubjectId.value = ''
      selectedTopicId.value = ''
      selectedLessonId.value = ''
      testSeriesList.value = []
      subjects.value = []
      topics.value = []
      lessons.value = []

      if (!selectedExamId.value) return

      loadingOptions.value = true
      try {
        if (selectedParentType.value === 'series') {
          const res = await api.get(`/v1/admin/exams/${selectedExamId.value}/testseries`)
          testSeriesList.value = res.data.testSeries || []
        } else {
          const res = await api.get(`/v1/admin/exams/${selectedExamId.value}/subjects`)
          subjects.value = res.data.subjects || []
        }
      } catch (err) {
        console.error('Failed to fetch options:', err)
      } finally {
        loadingOptions.value = false
      }
    }

    const onSubjectChange = async () => {
      selectedTopicId.value = ''
      selectedLessonId.value = ''
      topics.value = []
      lessons.value = []

      if (!selectedSubjectId.value) return

      loadingOptions.value = true
      try {
        const res = await api.get(`/v1/admin/subjects/${selectedSubjectId.value}/topics`)
        topics.value = res.data.topics || []
      } catch (err) {
        console.error('Failed to fetch topics:', err)
      } finally {
        loadingOptions.value = false
      }
    }

    const onTopicChange = async () => {
      selectedLessonId.value = ''
      lessons.value = []

      if (!selectedTopicId.value) return

      if (selectedParentType.value === 'lesson') {
        loadingOptions.value = true
        try {
          const res = await api.get(`/v1/admin/topics/${selectedTopicId.value}/lessons`)
          lessons.value = res.data.lessons || []
        } catch (err) {
          console.error('Failed to fetch lessons:', err)
        } finally {
          loadingOptions.value = false
        }
      }
    }

    const fetchParentData = async () => {
      try {
        if (lessonIdFromRoute) {
          const res = await api.get(`/v1/admin/lessons/${lessonIdFromRoute}`)
          parentData.value = res.data.lesson
        } else if (seriesIdFromRoute) {
          const res = await api.get(`/v1/admin/testseries/${seriesIdFromRoute}`)
          parentData.value = res.data.testSeries
        } else if (topicIdFromRoute) {
          const res = await api.get(`/v1/admin/topics/${topicIdFromRoute}`)
          parentData.value = res.data.topic
        }
      } catch (err) {
        console.error('Failed to fetch parent data:', err)
      }
    }

    const handleSubmit = async () => {
      if (!canSubmit.value) return

      loading.value = true
      try {
        const payload = {
          test: {
            ...form.value,
            lessonId: lessonIdFromRoute || selectedLessonId.value || null,
            seriesId: seriesIdFromRoute || selectedSeriesId.value || null,
            topicId: topicIdFromRoute || (selectedParentType.value === 'topic' ? selectedTopicId.value : null) || null
          }
        }
        
        const response = await api.post('/v1/admin/tests', payload)
        alert.success('Success', 'Test created successfully')
        
        // Navigate to the test questions page to add questions
        const testId = response.data.test?.id || response.data.id
        if (testId) {
          router.push(`/app/tests/${testId}/questions`)
        } else {
          router.push(backUrl.value)
        }
      } catch (err) {
        alert.error('Error', err.response?.data?.message || 'Failed to create test')
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      if (hasParentFromRoute.value) {
        fetchParentData()
      } else {
        fetchExams()
      }
    })

    return { 
      form, 
      loading, 
      loadingOptions,
      breadcrumbs, 
      backUrl, 
      parentData,
      parentType,
      parentTypeLabel,
      parentTypes,
      hasParentFromRoute,
      selectedParentType,
      selectedExamId,
      selectedSeriesId,
      selectedSubjectId,
      selectedTopicId,
      selectedLessonId,
      exams,
      testSeriesList,
      subjects,
      topics,
      lessons,
      isParentSelected,
      canSubmit,
      selectParentType,
      onExamChange,
      onSubjectChange,
      onTopicChange,
      handleSubmit 
    }
  }
}
</script>

<style scoped>
.parent-selection-section {
  background: #f8fafc;
  padding: 1.25rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.section-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 0.5rem;
}

.parent-type-selector {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.parent-type-option {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  color: #64748b;
}

.parent-type-option:hover {
  border-color: #6366f1;
  color: #6366f1;
}

.parent-type-option.active {
  border-color: #6366f1;
  background: #eef2ff;
  color: #6366f1;
}

.parent-type-option span {
  font-size: 0.875rem;
  font-weight: 500;
}

.cascading-selectors {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
}

.alert-warning {
  display: flex;
  align-items: center;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  color: #92400e;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
}

/* Dark mode */
:global(body.dark-mode) .parent-selection-section {
  background: #0f172a;
  border-color: #334155;
}

:global(body.dark-mode) .section-title {
  color: #f1f5f9;
}

:global(body.dark-mode) .parent-type-option {
  background: #1e293b;
  border-color: #334155;
  color: #94a3b8;
}

:global(body.dark-mode) .parent-type-option:hover,
:global(body.dark-mode) .parent-type-option.active {
  border-color: #6366f1;
  background: #1e1b4b;
  color: #a5b4fc;
}

:global(body.dark-mode) .cascading-selectors {
  border-color: #334155;
}

:global(body.dark-mode) .alert-warning {
  background: #422006;
  border-color: #854d0e;
  color: #fcd34d;
}
</style>
